import React, { useEffect, useState } from 'react';
import SummaryApi from '../common';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
  FaSearch,
  FaPhoneAlt,
  FaUserTie,
  FaCalendarCheck,
  FaQuoteLeft,
  FaChevronLeft,
  FaChevronRight,
  FaFilter
} from 'react-icons/fa';
import { IoMdCheckmarkCircleOutline } from "react-icons/io";

// ────────────────────────────────────────────────
// THEME — same rules as other files
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionGreen: '#10B981',
  actionRed: '#EF4444',
  actionRedHover: '#DC2626',
};

const ClosedLeads = () => {
  const [closedLeads, setClosedLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRange, setSelectedRange] = useState('All');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const fetchClosedLeads = async () => {
    setLoading(true);
    try {
      const headers = {
        'Authorization': 'Bearer sandeep_university_916',
        'Content-Type': 'application/json',
      };

      const response = await fetch(SummaryApi.getClosingLead.url, {
        method: SummaryApi.getClosingLead.method,
        headers: headers,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status) {
          const sortedData = (data.data || []).sort((a, b) =>
            new Date(b.created_date) - new Date(a.created_date)
          );
          setClosedLeads(sortedData);
          setFilteredLeads(sortedData);
        }
      } else {
        toast.error("Failed to fetch closed leads.");
      }
    } catch (error) {
      console.error("Error fetching closed leads:", error);
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClosedLeads();
  }, []);

  useEffect(() => {
    let tempLeads = closedLeads;

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      tempLeads = tempLeads.filter(lead =>
        lead.student_name?.toLowerCase().includes(lowerQuery) ||
        lead.employee_name?.toLowerCase().includes(lowerQuery) ||
        lead.mobile_number1?.includes(lowerQuery)
      );
    }

    if (selectedRange !== 'All') {
      const now = new Date();
      const todayStart = new Date(now.setHours(0, 0, 0, 0));
      const todayEnd = new Date(now.setHours(23, 59, 59, 999));

      tempLeads = tempLeads.filter(lead => {
        const leadDate = new Date(lead.created_date);

        if (selectedRange === 'today') {
          return leadDate >= todayStart && leadDate <= todayEnd;
        }
        else if (selectedRange === 'yesterday') {
          const yesterdayStart = new Date(todayStart);
          yesterdayStart.setDate(yesterdayStart.getDate() - 1);
          const yesterdayEnd = new Date(todayEnd);
          yesterdayEnd.setDate(yesterdayEnd.getDate() - 1);
          return leadDate >= yesterdayStart && leadDate <= yesterdayEnd;
        }
        else if (selectedRange === 'thisWeek') {
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - now.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          return leadDate >= startOfWeek && leadDate <= todayEnd;
        }
        else if (selectedRange === 'lastWeek') {
          const startOfLastWeek = new Date(now);
          startOfLastWeek.setDate(now.getDate() - now.getDay() - 7);
          startOfLastWeek.setHours(0, 0, 0, 0);

          const endOfLastWeek = new Date(startOfLastWeek);
          endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
          endOfLastWeek.setHours(23, 59, 59, 999);

          return leadDate >= startOfLastWeek && leadDate <= endOfLastWeek;
        }
        else if (selectedRange === 'custom' && fromDate && toDate) {
          const customStart = new Date(fromDate);
          customStart.setHours(0, 0, 0, 0);
          const customEnd = new Date(toDate);
          customEnd.setHours(23, 59, 59, 999);
          return leadDate >= customStart && leadDate <= customEnd;
        }
        return true;
      });
    }

    setFilteredLeads(tempLeads);
    setCurrentPage(1);
  }, [searchQuery, selectedRange, fromDate, toDate, closedLeads]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    }).format(date);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
  };

  const handleRangeChange = (e) => {
    setSelectedRange(e.target.value);
    if (e.target.value !== 'custom') {
      setFromDate(null);
      setToDate(null);
    }
  };

  return (
    <div className="min-h-screen">

      {/* HEADER */}
      <div className="max-w-8xl mx-auto mb-8">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1
                className="text-3xl font-bold flex items-center gap-2"
                style={{ color: THEME.primary }}
              >
                <IoMdCheckmarkCircleOutline style={{ color: THEME.actionGreen }} />
                Closed Leads
              </h1>
              <p className="text-gray-500 mt-1">
                Successfully converted students.
                <span
                  className="ml-2 px-2 py-0.5 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    color: '#065F46',
                  }}
                >
                  Total: {filteredLeads.length}
                </span>
              </p>
            </div>

            <div className="relative w-full md:w-1/3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:bg-white transition"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-100">
            <div
              className="flex items-center text-sm font-semibold"
              style={{ color: THEME.primary }}
            >
              <FaFilter className="mr-2" /> Filter Date:
            </div>

            <select
              value={selectedRange}
              onChange={handleRangeChange}
              style={{ color: THEME.primary }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#172C57] cursor-pointer"
            >
              <option value="All">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="thisWeek">This Week</option>
              <option value="lastWeek">Last Week</option>
              <option value="custom">Custom Range</option>
            </select>

            {selectedRange === 'custom' && (
              <div className="flex items-center gap-2 animate-fadeIn">
                <DatePicker
                  selected={fromDate}
                  onChange={date => setFromDate(date)}
                  selectsStart
                  startDate={fromDate}
                  endDate={toDate}
                  placeholderText="From Date"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-32 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                />
                <span className="text-gray-400">-</span>
                <DatePicker
                  selected={toDate}
                  onChange={date => setToDate(date)}
                  selectsEnd
                  startDate={fromDate}
                  endDate={toDate}
                  minDate={fromDate}
                  placeholderText="To Date"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-32 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                />
              </div>
            )}

            {(selectedRange !== 'All' || searchQuery) && (
              <button
                onClick={() => {
                  setSelectedRange('All');
                  setSearchQuery('');
                  setFromDate(null);
                  setToDate(null);
                }}
                style={{ color: THEME.actionRed }}
                onMouseEnter={(e) => (e.currentTarget.style.color = THEME.actionRedHover)}
                onMouseLeave={(e) => (e.currentTarget.style.color = THEME.actionRed)}
                className="ml-auto text-sm font-semibold transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-8xl mx-auto">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-2xl animate-pulse"></div>
            ))}
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-300">
            <div className="bg-gray-100 p-4 rounded-full mb-4">
              <FaSearch className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700">No leads found</h3>
            <p className="text-gray-500">Try adjusting your filters.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {currentLeads.map((lead) => (
                <div key={lead.close_lead_id} className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col">
                  <div className="h-2 bg-gradient-to-r from-green-500 to-emerald-400 w-full"></div>
                  <div className="p-6 flex-1 flex flex-col">

                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg"
                          style={{ backgroundColor: THEME.primaryLight, color: THEME.primary }}
                        >
                          {getInitials(lead.student_name)}
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-800 line-clamp-1" title={lead.student_name}>{lead.student_name}</h3>
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <FaPhoneAlt className="mr-1.5 text-xs" />
                            {lead.mobile_number1}
                          </div>
                        </div>
                      </div>
                      <div
                        className="text-xs px-2 py-1 rounded-md border font-semibold"
                        style={{
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          color: '#065F46',
                          borderColor: 'rgba(16, 185, 129, 0.2)',
                        }}
                      >
                        Closed
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl mb-4 relative flex-1">
                      <FaQuoteLeft className="absolute top-2 left-2 text-gray-200 text-xl" />
                      <p className="text-gray-600 text-sm italic relative z-10 pl-2">
                        {lead.description || "No description provided."}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center gap-2" title={`Employee ID: ${lead.employee_id}`}>
                          <div className="bg-orange-100 p-1.5 rounded-full text-orange-600"><FaUserTie /></div>
                          <span className="font-medium text-gray-700">By: {lead.full_name || "Unknown"}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FaCalendarCheck className="text-gray-400" />
                          <span>{formatDate(lead.created_date)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                <div className="text-sm text-gray-500 mb-4 sm:mb-0">
                  Showing <span className="font-semibold text-gray-900">{indexOfFirstItem + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(indexOfLastItem, filteredLeads.length)}</span> of <span className="font-semibold text-gray-900">{filteredLeads.length}</span> entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg border transition-colors ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}`}
                    style={currentPage !== 1 ? { color: THEME.primary } : {}}
                  >
                    <FaChevronLeft />
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => {
                      if (totalPages > 7 && (number < currentPage - 1 || number > currentPage + 1) && number !== 1 && number !== totalPages) {
                        if (number === currentPage - 2 || number === currentPage + 2) return <span key={number} className="px-2 text-gray-400">...</span>;
                        return null;
                      }
                      return (
                        <button
                          key={number}
                          onClick={() => paginate(number)}
                          style={
                            currentPage === number
                              ? { backgroundColor: THEME.primary, borderColor: THEME.primary }
                              : { color: THEME.primary }
                          }
                          className={`w-8 h-8 rounded-lg text-sm font-semibold transition-colors ${currentPage === number ? 'text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
                        >
                          {number}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg border transition-colors ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}`}
                    style={currentPage !== totalPages ? { color: THEME.primary } : {}}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClosedLeads;